import { useTranslation } from 'react-i18next';
import { InputField } from '../../UI';
import { Toggle } from '../../UI/base';
import { BookingDateTimeFields } from './BookingDateTimeFields';
import { BookingRecurrenceFields } from './BookingRecurrenceFields';
import { BookingSkipDatesField } from './BookingSkipDatesField';
import type { BookingFormState } from './useBookingFormState';

interface BookingOccurrenceFieldsProps {
  form: BookingFormState;
  /** Only creation exposes the Repeat toggle — the recurrence kind is fixed once saved. */
  isCreating: boolean;
  /** i18n key prefix for the labels, e.g. `patient-booking`. */
  keyPrefix: string;
}

// The date/time → repeat → recurrence → skip-dates → reason block shared by every occurrence-based
// booking form. Driven entirely by the useBookingFormState hook passed in as `form`.
export const BookingOccurrenceFields = ({
  form,
  isCreating,
  keyPrefix
}: BookingOccurrenceFieldsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <BookingDateTimeFields
        date={form.date}
        startTime={form.startTime}
        endTime={form.endTime}
        dateLabel={form.isRecurring ? t(`${keyPrefix}-start-date-label`) : undefined}
        dateError={form.errors.date}
        timeError={form.errors.time}
        onDateChange={form.setDate}
        onStartTimeChange={form.setStartTime}
        onEndTimeChange={form.setEndTime}
      />

      {isCreating && (
        <Toggle
          label={t(`${keyPrefix}-repeat-label`)}
          checked={form.isRecurring}
          onChange={form.handleToggleRecurring}
        />
      )}

      {form.isRecurring && (
        <>
          <BookingRecurrenceFields
            selectedDays={form.selectedDays}
            intervalWeeks={form.intervalWeeks}
            endDate={form.recurrenceEndDate}
            errors={form.errors}
            firstOccurrence={form.firstOccurrence}
            onDaysChange={form.setSelectedDays}
            onIntervalChange={form.setIntervalWeeks}
            onEndDateChange={form.setRecurrenceEndDate}
          />
          <BookingSkipDatesField
            startDate={form.date}
            daysOfWeek={form.selectedDays}
            intervalWeeks={form.intervalWeeks}
            endDate={form.recurrenceEndDate}
            exceptionDates={form.exceptionDates}
            onChange={form.setExceptionDates}
          />
        </>
      )}

      <InputField
        label={t(`${keyPrefix}-reason-label`)}
        type="text"
        value={form.reason}
        placeholder={t(`${keyPrefix}-reason-placeholder`)}
        onChange={(e) => form.setReason(e.target.value)}
      />
    </>
  );
};
